/**
 * Billing dashboard page
 * Displays billing statistics, balance, and transaction history
 * Deposit is handled in separate TopupPage at /admin/topup
 */
import React, { useState, useEffect } from 'react';
import { useBilling, BillingStats } from '@/hooks/useBilling';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { collection, query, where, orderBy, limit, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Calendar, DollarSign, Users, FileImage, CreditCard, TrendingUp, TrendingDown, RefreshCw, QrCode } from 'lucide-react';
import PaymentAmountModal from '@/components/admin/PaymentAmountModal';
import PaymentModal from '@/components/admin/PaymentModal';
import { paymentApi } from '@/services/paymentApi';

interface Transaction {
  id: string;
  transaction_id: string;
  type: 'deposit' | 'charge';
  amount: number;
  description: string;
  image_id?: string;
  mockup_id?: string;
  visitor_id?: string;
  session_id?: string;
  created_at: Timestamp | { seconds: number; nanoseconds: number };
  created_by?: string;
}

export default function BillingDashboard() {
  const { adminUser } = useAdminAuth();
  const { balance, loading: balanceLoading, getStats } = useBilling();
  const [stats, setStats] = useState<BillingStats | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Filter state (for UI only - doesn't trigger API calls)
  const [filterType, setFilterType] = useState<'day' | 'week' | 'month' | 'custom'>('day');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [visitorId, setVisitorId] = useState<string>('');

  // Payment state
  const [showAmountModal, setShowAmountModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentOrder, setPaymentOrder] = useState<any>(null);


  // Load stats once on component mount
  useEffect(() => {
    const loadStats = async () => {
      try {
        // Load stats for today
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const endOfDay = new Date(now);
        endOfDay.setHours(23, 59, 59, 999);

        const startISO = start.toISOString();
        const endISO = endOfDay.toISOString();

        // Fetch stats for today
        const todayStats = await getStats(startISO, endISO);
        setStats(todayStats);
      } catch (err) {
        console.error('Error loading stats:', err);
        setStats(null);
      }
    };

    loadStats();
  }, []); // Only run once on mount

  // Load transactions manually (no real-time updates - user controls refresh)
  const loadTransactions = async () => {
    setTransactionsLoading(true);
    try {
      const transactionsQuery = query(
        collection(db, 'billing_transactions'),
        orderBy('created_at', 'desc'),
        limit(100)
      );

      const snapshot = await getDocs(transactionsQuery);
      const trans: Transaction[] = [];
      snapshot.forEach((doc) => {
        trans.push({ id: doc.id, ...doc.data() } as Transaction);
      });
      setTransactions(trans);
      console.log('[BillingDashboard] Loaded', trans.length, 'transactions');
    } catch (err) {
      console.error('[BillingDashboard] Failed to load transactions:', err);
    } finally {
      setTransactionsLoading(false);
    }
  };

  // Load transactions once on mount
  useEffect(() => {
    loadTransactions();
  }, []);

  // Manual refresh handler
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      console.log('[BillingDashboard] Manual refresh requested');

      // Reload stats
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const endOfDay = new Date(now);
      endOfDay.setHours(23, 59, 59, 999);
      const todayStats = await getStats(start.toISOString(), endOfDay.toISOString());
      setStats(todayStats);

      // Reload transactions
      await loadTransactions();
    } catch (err) {
      console.error('[BillingDashboard] Refresh failed:', err);
      alert('Làm mới thất bại. Vui lòng thử lại.');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Payment handlers
  const handleAmountConfirm = async (amount: number) => {
    setShowAmountModal(false);

    try {
      const adminUserId = adminUser?.user?.uid;

      if (!adminUserId) {
        alert('Không tìm thấy thông tin admin user');
        return;
      }

      console.log('[BillingDashboard] Creating payment order:', amount);

      const result = await paymentApi.createOrder(amount, adminUserId);

      if (result.success) {
        setPaymentOrder(result.order);
        setShowPaymentModal(true);
        console.log('[BillingDashboard] Payment order created:', result.order.orderId);
      } else {
        alert('Tạo đơn thanh toán thất bại: ' + (result.error || 'Unknown error'));
      }
    } catch (error: any) {
      console.error('[BillingDashboard] Create order error:', error);
      alert('Lỗi: ' + error.message);
    }
  };

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    setPaymentOrder(null);

    console.log('[BillingDashboard] Payment successful, refreshing data...');

    // Refresh balance and transactions
    handleRefresh();

    // Show success message
    alert('✅ Nạp tiền thành công!');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN').format(amount) + ' đ';
  };

  const formatDate = (timestamp: Timestamp | { seconds: number; nanoseconds: number }) => {
    const date = timestamp instanceof Timestamp 
      ? timestamp.toDate() 
      : new Date(timestamp.seconds * 1000);
    return new Intl.DateTimeFormat('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Quản lý thanh toán</h1>
          <p className="text-gray-600">
            Dữ liệu hôm nay được tải khi mở trang. Nhấn "Làm mới" để cập nhật.
          </p>
        </div>
        <div className="flex gap-3">
          {/* Nút Nạp tiền qua QR */}
          <button
            onClick={() => setShowAmountModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
          >
            <QrCode className="w-5 h-5" />
            Nạp tiền qua QR
          </button>

          {/* Nút Làm mới */}
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Đang làm mới...' : 'Làm mới'}
          </button>
        </div>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Số tiền còn lại</p>
              <p className="text-2xl font-bold text-gray-900">
                {balanceLoading ? '...' : formatCurrency(balance?.balance || 0)}
              </p>
            </div>
            <DollarSign className="w-10 h-10 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Tổng đã nạp</p>
              <p className="text-2xl font-bold text-gray-900">
                {balanceLoading ? '...' : formatCurrency(balance?.total_deposited || 0)}
              </p>
            </div>
            <CreditCard className="w-10 h-10 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Đã sử dụng</p>
              <p className="text-2xl font-bold text-gray-900">
                {balanceLoading ? '...' : formatCurrency((balance?.total_deposited || 0) - (balance?.balance || 0))}
              </p>
            </div>
            <TrendingDown className="w-10 h-10 text-red-500" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex gap-2">
            <button
              onClick={() => setFilterType('day')}
              className={`px-4 py-2 rounded-lg ${
                filterType === 'day'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Hôm nay
            </button>
            <button
              onClick={() => setFilterType('week')}
              className={`px-4 py-2 rounded-lg ${
                filterType === 'week'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Tuần này
            </button>
            <button
              onClick={() => setFilterType('month')}
              className={`px-4 py-2 rounded-lg ${
                filterType === 'month'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Tháng này
            </button>
            <button
              onClick={() => setFilterType('custom')}
              className={`px-4 py-2 rounded-lg ${
                filterType === 'custom'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Tùy chọn
            </button>
          </div>

          {filterType === 'custom' && (
            <div className="flex gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
              />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
              />
            </div>
          )}

          <input
            type="text"
            value={visitorId}
            onChange={(e) => setVisitorId(e.target.value)}
            placeholder="Filter theo Visitor ID (tùy chọn)"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
          />
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-8 h-8 text-indigo-500" />
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-sm text-gray-600 mb-1">Số lượng người dùng</p>
            <p className="text-2xl font-bold text-gray-900">{stats.users_count}</p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <Calendar className="w-8 h-8 text-blue-500" />
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-sm text-gray-600 mb-1">Số session</p>
            <p className="text-2xl font-bold text-gray-900">{stats.sessions_count}</p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <FileImage className="w-8 h-8 text-purple-500" />
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-sm text-gray-600 mb-1">Số thiết kế chốt</p>
            <p className="text-2xl font-bold text-gray-900">{stats.designs_count}</p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <FileImage className="w-8 h-8 text-orange-500" />
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-sm text-gray-600 mb-1">Số lượng hình ảnh</p>
            <p className="text-2xl font-bold text-gray-900">{stats.images_count}</p>
            <p className="text-xs text-gray-500 mt-1">
              ({stats.image_edit_count} xóa nền + {stats.mockup_count} mockup)
            </p>
          </div>
        </div>
      )}

      {/* Total Cost */}
      {stats && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Tổng chi phí trong khoảng thời gian</p>
              <p className="text-3xl font-bold text-red-600">{formatCurrency(stats.total_cost)}</p>
            </div>
            <DollarSign className="w-12 h-12 text-red-500" />
          </div>
        </div>
      )}

      {/* Transaction History */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Lịch sử giao dịch</h2>
        </div>
        {transactionsLoading ? (
          <div className="text-center py-8">Đang tải giao dịch...</div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-8 text-gray-500">Chưa có giao dịch nào</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Thời gian
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Loại
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mô tả
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Số tiền
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(transaction.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          transaction.type === 'deposit'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {transaction.type === 'deposit' ? 'Nạp tiền' : 'Trừ tiền'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {transaction.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <span
                        className={
                          transaction.type === 'deposit' ? 'text-green-600' : 'text-red-600'
                        }
                      >
                        {transaction.type === 'deposit' ? '+' : '-'}
                        {formatCurrency(transaction.amount)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payment Modals */}
      <PaymentAmountModal
        isOpen={showAmountModal}
        onClose={() => setShowAmountModal(false)}
        onConfirm={handleAmountConfirm}
      />

      {paymentOrder && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setPaymentOrder(null);
          }}
          orderId={paymentOrder.orderId}
          qrUrl={paymentOrder.qrData.qrUrl}
          amount={paymentOrder.amount}
          accountInfo={{
            accountNo: paymentOrder.qrData.accountNo,
            bankName: paymentOrder.qrData.bankName,
            accountName: paymentOrder.qrData.accountName
          }}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
}

